import { CloudSun, Link2, Link2Off, MapPin, Music, Send, Sparkles, Wifi, WifiOff } from 'lucide-react'
import DeviceScreenPreview from '../../../../components/DeviceScreenPreview'
import { Button } from '../../../../components/ui/Button'
import { Card } from '../../../../components/ui/Card'
import { emotionLabelEntry } from '../../../../utils/emotion'
import { formatCloudMessageTime } from './cloudTestWs'

function ChatBubble({ dir, text, time }) {
  if (dir === 'sys') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[92%] rounded-full border-2 border-brand-textDark bg-brand-bg px-3 py-1.5 text-xs font-bold text-slate-700 shadow-[4px_4px_0px_0px_#E5BC2E] dark:bg-slate-900/30 dark:text-slate-200">
          <span className="mr-2 font-mono text-slate-500 dark:text-slate-400">[{time}]</span>
          {text}
        </div>
      </div>
    )
  }

  const outgoing = dir === 'out'
  const bubbleClass = outgoing
    ? 'bg-brand-pink text-white shadow-[4px_4px_0px_0px_#E04848] rounded-br-xl'
    : 'bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-100 shadow-[4px_4px_0px_0px_#40E0D0] rounded-bl-xl'

  return (
    <div className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] rounded-3xl border-2 border-brand-textDark px-4 py-3 ${bubbleClass}`}>
        <div className="whitespace-pre-wrap break-words text-sm font-bold leading-relaxed">{text}</div>
        <div className={`mt-1 font-mono text-xs ${outgoing ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>[{time}]</div>
      </div>
    </div>
  )
}

export function ConnectionCard({
  tx,
  targetDeviceId,
  setTargetDeviceId,
  wsConnected,
  connecting,
  retryIn,
  wsSessionId,
  wsUrl,
  deviceOnline,
  onConnect,
  onDisconnect,
  onClear,
}) {
  const statusText = wsConnected
    ? tx({ zh: '已连接', en: 'Connected' })
    : connecting
      ? tx({ zh: '连接中', en: 'Connecting' })
      : tx({ zh: '未连接', en: 'Disconnected' })
  const statusTone = wsConnected ? 'bg-emerald-500' : connecting ? 'bg-amber-500 animate-pulse' : 'bg-rose-500'

  return (
    <Card className="border-brand-textDark px-4 py-3 shadow-[4px_4px_0px_0px_#E5BC2E]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <label htmlFor="cloud-device-id" className="text-sm font-extrabold text-slate-900 dark:text-slate-100">device-id</label>
          <input
            id="cloud-device-id"
            value={targetDeviceId}
            onChange={(event) => setTargetDeviceId(event.target.value)}
            placeholder="public-test"
            className="min-w-[220px] flex-1 rounded-2xl border-2 border-slate-100 bg-white px-4 py-3 font-mono text-slate-800 outline-none focus:border-brand-yellow focus:ring-2 focus:ring-yellow-100 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-yellow-500/20"
          />
          <Button
            variant={wsConnected ? 'danger' : 'secondary'}
            size="md"
            onClick={wsConnected ? onDisconnect : onConnect}
            disabled={connecting}
          >
            {wsConnected ? <Link2Off className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {wsConnected ? tx({ zh: '断开', en: 'Disconnect' }) : tx({ zh: '连接', en: 'Connect' })}
          </Button>
          <Button variant="neutral" size="md" onClick={onClear}>{tx({ zh: '清空', en: 'Clear' })}</Button>
        </div>

        <div className="flex flex-shrink-0 flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            {wsConnected ? <Wifi className="h-4 w-4 text-emerald-600" /> : <WifiOff className="h-4 w-4 text-rose-600" />}
            <span className={`h-2.5 w-2.5 rounded-full ${statusTone}`} />
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-200">
              {statusText}{wsSessionId ? ` (${wsSessionId})` : ''}
              {!wsConnected && retryIn > 0 ? ` · ${tx({ zh: '{n}s 重试', en: 'retry in {n}s' }, { n: retryIn })}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${deviceOnline ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`} />
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-200">
              {deviceOnline ? tx({ zh: '设备在线', en: 'Device online' }) : tx({ zh: '设备离线', en: 'Device offline' })}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-2 truncate font-mono text-xs text-slate-500 dark:text-slate-400" title={wsUrl || ''}>{wsUrl || '-'}</div>
    </Card>
  )
}

export function CloudChatCard({ tx, wsConnected, sending, inputText, setInputText, chatItems, streamText, isTyping, chatEndRef, onSend }) {
  return (
    <Card className="flex min-h-[70vh] flex-col overflow-hidden border-brand-textDark p-0 shadow-[4px_4px_0px_0px_#E5BC2E]">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
        <div className="min-w-0">
          <div className="font-extrabold text-slate-900 dark:text-slate-100">{tx({ zh: '云端对话', en: 'Cloud chat' })}</div>
          <div className="truncate text-xs font-bold text-slate-500 dark:text-slate-400">
            {tx({ zh: '地理位置/天气不依赖设备在线；放歌需要设备在线。', en: 'Location/weather do not need device online; music requires device online.' })}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="neutral" size="sm" onClick={() => onSend('我在哪里')} disabled={!wsConnected}><MapPin className="h-4 w-4" />{tx({ zh: '地理位置', en: 'Location' })}</Button>
          <Button variant="neutral" size="sm" onClick={() => onSend('天气怎么样')} disabled={!wsConnected}><CloudSun className="h-4 w-4" />{tx({ zh: '天气', en: 'Weather' })}</Button>
          <Button variant="neutral" size="sm" onClick={() => onSend('放歌 轻音乐')} disabled={!wsConnected}><Music className="h-4 w-4" />{tx({ zh: '放歌', en: 'Music' })}</Button>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-6">
        {chatItems.length === 0 && !streamText && !isTyping ? (
          <div className="text-sm font-bold text-slate-400">{tx({ zh: '还没有消息。先连接云端，然后点右上角按钮或输入一句话。', en: 'No messages yet. Connect first, then click the buttons or type a message.' })}</div>
        ) : null}
        {chatItems.map((item, index) => <ChatBubble key={`${item.time}-${index}`} {...item} />)}
        {streamText ? <ChatBubble dir="in" text={streamText} time={formatCloudMessageTime()} /> : null}
        {isTyping && !streamText ? (
          <div className="flex justify-start">
            <div className="max-w-[88%] rounded-3xl rounded-bl-xl border-2 border-brand-textDark bg-white px-4 py-3 shadow-[4px_4px_0px_0px_#40E0D0] dark:bg-slate-950">
              <div className="flex gap-1">
                {[0, 150, 300].map((delay) => <span key={delay} className="h-2 w-2 animate-bounce rounded-full bg-slate-300 dark:bg-slate-600" style={{ animationDelay: `${delay}ms` }} />)}
              </div>
            </div>
          </div>
        ) : null}
        <div ref={chatEndRef} />
      </div>

      <form className="flex flex-col gap-3 border-t border-slate-100 p-4 sm:flex-row dark:border-slate-800" onSubmit={(event) => { event.preventDefault(); onSend() }}>
        <input
          value={inputText}
          onChange={(event) => setInputText(event.target.value)}
          placeholder={wsConnected ? tx({ zh: '输入一句话，回车发送…', en: 'Type a message and press Enter…' }) : tx({ zh: '未连接云端，无法发送', en: 'Disconnected; cannot send' })}
          className="flex-1 rounded-2xl border-2 border-slate-100 bg-white px-4 py-3 text-slate-800 outline-none focus:border-brand-pink focus:ring-2 focus:ring-rose-100 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-rose-500/20"
          disabled={!wsConnected || sending}
        />
        <Button type="submit" variant="primary" size="md" disabled={!wsConnected || sending || !String(inputText || '').trim()}><Send className="h-4 w-4" />{tx({ zh: '发送', en: 'Send' })}</Button>
      </form>
    </Card>
  )
}

export function EyesPreviewCard({ tx, deviceId, eyeTheme, setEyeTheme, eyeColor, setEyeColor, emotion, emotionEmoji, previewPoke, setPreviewPoke }) {
  const themes = ['robo', 'chamii', 'miaoban', 'raw', 'oled', 'classic', 'ani']
  const colors = [
    { name: 'Yellow', color: '#FFD233' },
    { name: 'Cyan', color: '#40E0D0' },
    { name: 'Pink', color: '#FF5C5C' },
    { name: 'Orange', color: '#FF9800' },
    { name: 'White', color: '#ffffff' },
  ]

  return (
    <Card className="border-brand-textDark p-5 shadow-[4px_4px_0px_0px_#40E0D0]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2"><Sparkles className="h-5 w-5 text-slate-500 dark:text-slate-300" /><div className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">{tx({ zh: '眼睛预览', en: 'Eyes preview' })}</div></div>
        <div className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{deviceId}</div>
      </div>
      <div className="flex items-center justify-center overflow-hidden rounded-2xl border-2 border-brand-textDark bg-brand-bg p-3 shadow-[4px_4px_0px_0px_#FFD233] dark:bg-slate-900/30">
        <DeviceScreenPreview theme={eyeTheme} emotion={emotion} poke={previewPoke} accent={eyeColor} />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100"><span className="mr-2">{emotionEmoji}</span>{tx(emotionLabelEntry(emotion))}</div>
        <Button variant="neutral" size="sm" onClick={() => setPreviewPoke((value) => (value + 1) % 1000000)}>{tx({ zh: '自检', en: 'Check' })}</Button>
      </div>
      <details className="mt-3 text-xs text-slate-500 dark:text-slate-300">
        <summary className="cursor-pointer select-none font-bold">{tx({ zh: '预览设置', en: 'Preview settings' })}</summary>
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {themes.map((theme) => (
              <button key={theme} type="button" onClick={() => setEyeTheme(theme)} className={`rounded-2xl border-2 px-3 py-2 font-extrabold transition-colors ${eyeTheme === theme ? 'border-brand-textDark bg-brand-yellow text-brand-textDark shadow-[4px_4px_0px_0px_#E5BC2E]' : 'border-slate-100 bg-white text-slate-600 hover:bg-brand-bg dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-900'}`}>{theme === 'robo' ? 'Robo' : theme === 'oled' ? 'OLED' : theme[0].toUpperCase() + theme.slice(1)}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {colors.map(({ name, color }) => (
              <button key={color} type="button" onClick={() => setEyeColor(color)} className={`h-9 w-9 rounded-2xl border-2 transition-colors ${eyeColor === color ? 'border-brand-textDark shadow-[4px_4px_0px_0px_#CBD5E1]' : 'border-slate-100 hover:border-slate-200 dark:border-slate-800'}`} title={name} aria-label={name}>
                <span className="block h-full w-full rounded-[14px]" style={{ backgroundColor: color }} />
              </button>
            ))}
            <div className="font-mono text-xs text-slate-500 dark:text-slate-400">{eyeColor}</div>
          </div>
        </div>
      </details>
    </Card>
  )
}
